/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import * as orgAdmin from "../../api/orgAdmin";
import { formatDate } from "../apps/drive/format";

const ENTITIES = [
  { value: "", label: "All activity" },
  { value: "file", label: "Files" },
  { value: "folder", label: "Folders" },
  { value: "share", label: "Shares" },
  { value: "team", label: "Team folders" },
  { value: "team_member", label: "Team membership" },
];

const ACTION_COLOR = { created: "success", updated: "default", deleted: "error", downloaded: "info" };

/**
 * Derived from created_at/updated_at/updated_by on the drive tables, not a
 * dedicated event log — so this can show what changed and who touched it
 * last, but never previews or downloads (those leave no row to diff).
 */
const AdminActivity = () => {
  const theme = useTheme();
  const [entity, setEntity] = useState("");
  const [state, setState] = useState({ loading: true, activity: [], error: null });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      setState({ loading: false, activity: await orgAdmin.getActivity({ entity: entity || undefined }), error: null });
    } catch (err) {
      setState({ loading: false, activity: [], error: err.message || "Could not load activity." });
    }
  }, [entity]);

  useEffect(() => {
    load();
  }, [load]);

  const actorLabel = (row) => row.actor_name || row.actor_email || (row.actor_id ? `user ${row.actor_id.slice(0, 8)}…` : "—");

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>Activity</Typography>
          <Typography sx={{ fontSize: 12.5, color: theme.palette.secondaryText.color }}>
            Recent changes across this organisation's files, folders, shares, and teams.
          </Typography>
        </Box>
        <Select
          size="small"
          value={entity}
          onChange={(e) => setEntity(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170 }}
        >
          {ENTITIES.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {state.error && <Alert severity="error" sx={{ mb: 2 }}>{state.error}</Alert>}

      {state.loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "accent.main" }} />
        </Box>
      ) : (
        <Paper variant="outlined">
          {state.activity.length === 0 ? (
            <Typography sx={{ p: 4, textAlign: "center", fontSize: 13.5, color: theme.palette.secondaryText.color }}>
              No activity to show.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell width={130}>Type</TableCell>
                  <TableCell width={110}>Action</TableCell>
                  <TableCell width={180}>By</TableCell>
                  <TableCell width={160}>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {state.activity.map((row) => (
                  <TableRow key={`${row.entity}-${row.id}`} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap>{row.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={row.entity} sx={{ fontSize: 10.5, textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.action}
                        color={ACTION_COLOR[row.action] || "default"}
                        variant={row.action === "updated" ? "outlined" : "filled"}
                        sx={{ fontSize: 10.5, textTransform: "capitalize" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                          {actorLabel(row).charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography sx={{ fontSize: 12 }} noWrap>{actorLabel(row)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(row.at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default AdminActivity;
